import React, { useEffect, useState } from 'react';
import { useTheme } from '../../context/ThemeContext';
import { formatCurrency, CURRENCY } from '../../config/currency';
import { useCountryConfig } from '../../hooks/useCountryConfig';
import { isSingleTax, singleTaxLabel } from '../../config/tax';
import api from '../../api/api';
import {
  BanknotesIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  ReceiptPercentIcon,
  TicketIcon,
  CreditCardIcon,
  CurrencyRupeeIcon,
} from '@heroicons/react/24/outline';
import { LoadingSpinner } from '../LoadingSpinner';

interface FinancialSummaryProps {
  vendorId: number;
  startDate: string;
  endDate: string;
}

interface TaxBreakdown {
  cgst: number;
  sgst: number;
  igst: number;
  serviceTax: number;
  total: number;
}

interface PaymentMethod {
  method: string;
  amount: number;
  count: number;
  percentage: number;
}

interface DailyRevenue {
  date: string;
  revenue: number;
  orders: number;
}

interface FinancialData {
  grossRevenue: number;
  netRevenue: number;
  totalTax: number;
  totalDiscounts: number;
  revenueGrowth: number;
  previousPeriodRevenue: number;
  taxBreakdown: TaxBreakdown;
  paymentMethods: PaymentMethod[];
  dailyRevenue: DailyRevenue[];
}

const FinancialSummary: React.FC<FinancialSummaryProps> = ({ vendorId, startDate, endDate }) => {
  const { theme } = useTheme();
  const { taxSublabels, taxLabel } = useCountryConfig();
  const singleTax = isSingleTax(taxSublabels);
  const [data, setData] = useState<FinancialData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchFinancialSummary = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await api.get(
          `/api/vendors/${vendorId}/reports/financial-summary`,
          {
            params: { startDate, endDate },
          }
        );
        setData(response.data);
      } catch (err: unknown) {
        console.error('Failed to fetch financial summary:', err);
        setError('Failed to load financial data. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchFinancialSummary();
  }, [vendorId, startDate, endDate]);

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div
        className="glassmorphism-card p-8 text-center"
        style={{
          background: theme.components.card.background,
          border: `1px solid ${theme.components.card.border}`,
          borderRadius: '12px',
        }}
      >
        <div className="text-red-500 mb-2">
          <svg
            className="w-12 h-12 mx-auto"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
        </div>
        <p style={{ color: theme.text }}>{error}</p>
      </div>
    );
  }

  if (!data) {
    return null;
  }

  const maxDailyRevenue = Math.max(...data.dailyRevenue.map((d) => d.revenue), 1);

  return (
    <div className="space-y-6">
      {/* Header Cards - Revenue Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Gross Revenue - Large Card */}
        <div
          className="glassmorphism-card p-6 lg:col-span-2"
          style={{
            background: theme.components.card.background,
            border: `1px solid ${theme.components.card.border}`,
            borderRadius: '12px',
          }}
        >
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <BanknotesIcon className="w-6 h-6" style={{ color: theme.primary }} />
                <p className="text-sm font-medium" style={{ color: theme.gray[600] }}>
                  Gross Revenue
                </p>
              </div>
              <h2 className="text-4xl font-bold mb-1" style={{ color: theme.text }}>
                {formatCurrency(data.grossRevenue)}
              </h2>
              <div className="flex items-center gap-2 mt-2">
                {data.revenueGrowth >= 0 ? (
                  <ArrowTrendingUpIcon className="w-5 h-5 text-green-500" />
                ) : (
                  <ArrowTrendingDownIcon className="w-5 h-5 text-red-500" />
                )}
                <span
                  className="text-sm font-semibold"
                  style={{ color: (data.revenueGrowth ?? 0) >= 0 ? '#10B981' : '#EF4444' }}
                >
                  {(data.revenueGrowth ?? 0) >= 0 ? '+' : ''}
                  {(data.revenueGrowth ?? 0).toFixed(1)}%
                </span>
                <span className="text-xs" style={{ color: theme.gray[500] }}>
                  vs previous period
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Net Revenue */}
        <div
          className="glassmorphism-card p-6"
          style={{
            background: theme.components.card.background,
            border: `1px solid ${theme.components.card.border}`,
            borderRadius: '12px',
          }}
        >
          <div className="flex items-center gap-2 mb-2">
            <CurrencyRupeeIcon className="w-5 h-5" style={{ color: theme.success }} />
            <p className="text-sm font-medium" style={{ color: theme.gray[600] }}>
              Net Revenue
            </p>
          </div>
          <h3 className="text-2xl font-bold" style={{ color: theme.text }}>
            {formatCurrency(data.netRevenue)}
          </h3>
          <p className="text-xs mt-1" style={{ color: theme.gray[500] }}>
            After tax & discounts
          </p>
        </div>

        {/* Total Tax */}
        <div
          className="glassmorphism-card p-6"
          style={{
            background: theme.components.card.background,
            border: `1px solid ${theme.components.card.border}`,
            borderRadius: '12px',
          }}
        >
          <div className="flex items-center gap-2 mb-2">
            <ReceiptPercentIcon className="w-5 h-5" style={{ color: theme.warning }} />
            <p className="text-sm font-medium" style={{ color: theme.gray[600] }}>
              Total Tax
            </p>
          </div>
          <h3 className="text-2xl font-bold" style={{ color: theme.text }}>
            {formatCurrency(data.totalTax)}
          </h3>
          <p className="text-xs mt-1" style={{ color: theme.gray[500] }}>
            Collected
          </p>
        </div>

        {/* Total Discounts */}
        <div
          className="glassmorphism-card p-6"
          style={{
            background: theme.components.card.background,
            border: `1px solid ${theme.components.card.border}`,
            borderRadius: '12px',
          }}
        >
          <div className="flex items-center gap-2 mb-2">
            <TicketIcon className="w-5 h-5" style={{ color: theme.danger }} />
            <p className="text-sm font-medium" style={{ color: theme.gray[600] }}>
              Total Discounts
            </p>
          </div>
          <h3 className="text-2xl font-bold" style={{ color: theme.text }}>
            {formatCurrency(data.totalDiscounts)}
          </h3>
          <p className="text-xs mt-1" style={{ color: theme.gray[500] }}>
            Applied
          </p>
        </div>

        {/* Previous Period Revenue */}
        <div
          className="glassmorphism-card p-6"
          style={{
            background: theme.components.card.background,
            border: `1px solid ${theme.components.card.border}`,
            borderRadius: '12px',
          }}
        >
          <div className="flex items-center gap-2 mb-2">
            <BanknotesIcon className="w-5 h-5" style={{ color: theme.gray[500] }} />
            <p className="text-sm font-medium" style={{ color: theme.gray[600] }}>
              Previous Period
            </p>
          </div>
          <h3 className="text-2xl font-bold" style={{ color: theme.text }}>
            {formatCurrency(data.previousPeriodRevenue)}
          </h3>
          <p className="text-xs mt-1" style={{ color: theme.gray[500] }}>
            For comparison
          </p>
        </div>
      </div>

      {/* Daily Revenue Chart */}
      <div
        className="glassmorphism-card p-6"
        style={{
          background: theme.components.card.background,
          border: `1px solid ${theme.components.card.border}`,
          borderRadius: '12px',
        }}
      >
        <h3 className="text-lg font-semibold mb-4" style={{ color: theme.text }}>
          Daily Revenue Trend
        </h3>
        <div className="space-y-3">
          {data.dailyRevenue.length > 0 ? (
            data.dailyRevenue.map((day, index) => {
              const heightPercentage = (day.revenue / maxDailyRevenue) * 100;
              return (
                <div key={index} className="flex items-center gap-3">
                  <div
                    className="w-24 text-xs font-medium"
                    style={{ color: theme.gray[600] }}
                  >
                    {new Date(day.date).toLocaleDateString(CURRENCY.locale, {
                      month: 'short',
                      day: 'numeric',
                    })}
                  </div>
                  <div className="flex-1 relative">
                    <div
                      className="h-8 rounded transition-all duration-300 hover:opacity-80"
                      style={{
                        width: `${heightPercentage}%`,
                        background: `linear-gradient(90deg, ${theme.primary}, ${theme.accent})`,
                        minWidth: '2px',
                      }}
                    />
                  </div>
                  <div className="w-28 text-right">
                    <div className="text-sm font-semibold" style={{ color: theme.text }}>
                      {formatCurrency(day.revenue)}
                    </div>
                    <div className="text-xs" style={{ color: theme.gray[500] }}>
                      {day.orders} orders
                    </div>
                  </div>
                </div>
              );
            })
          ) : (
            <p className="text-center py-8" style={{ color: theme.gray[500] }}>
              No revenue data available for this period
            </p>
          )}
        </div>
      </div>

      {/* Payment Methods and Tax Breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Payment Methods Breakdown */}
        <div
          className="glassmorphism-card p-6"
          style={{
            background: theme.components.card.background,
            border: `1px solid ${theme.components.card.border}`,
            borderRadius: '12px',
          }}
        >
          <div className="flex items-center gap-2 mb-4">
            <CreditCardIcon className="w-5 h-5" style={{ color: theme.primary }} />
            <h3 className="text-lg font-semibold" style={{ color: theme.text }}>
              Payment Methods
            </h3>
          </div>
          <div className="space-y-4">
            {data.paymentMethods.length > 0 ? (
              data.paymentMethods.map((method, index) => (
                <div key={index}>
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-sm font-medium" style={{ color: theme.text }}>
                      {method.method}
                    </span>
                    <span className="text-sm font-semibold" style={{ color: theme.text }}>
                      {formatCurrency(method.amount)}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div
                      className="flex-1 h-2 rounded-full"
                      style={{ background: theme.gray[200] }}
                    >
                      <div
                        className="h-2 rounded-full transition-all duration-300"
                        style={{
                          width: `${method.percentage}%`,
                          background: theme.primary,
                        }}
                      />
                    </div>
                    <span className="text-xs font-medium" style={{ color: theme.gray[600] }}>
                      {(method.percentage ?? 0).toFixed(1)}%
                    </span>
                  </div>
                  <div className="text-xs mt-1" style={{ color: theme.gray[500] }}>
                    {method.count} transactions
                  </div>
                </div>
              ))
            ) : (
              <p className="text-center py-4" style={{ color: theme.gray[500] }}>
                No payment data available
              </p>
            )}
          </div>
        </div>

        {/* Tax Breakdown */}
        <div
          className="glassmorphism-card p-6"
          style={{
            background: theme.components.card.background,
            border: `1px solid ${theme.components.card.border}`,
            borderRadius: '12px',
          }}
        >
          <div className="flex items-center gap-2 mb-4">
            <ReceiptPercentIcon className="w-5 h-5" style={{ color: theme.warning }} />
            <h3 className="text-lg font-semibold" style={{ color: theme.text }}>
              Tax Breakdown
            </h3>
          </div>
          <div className="space-y-3">
            {singleTax ? (
              /* Single-tax countries (e.g. UK VAT): one consolidated row */
              <div className="flex justify-between items-center p-3 rounded-lg" style={{ background: theme.gray[50] }}>
                <div>
                  <p className="text-sm font-medium" style={{ color: theme.text }}>
                    {singleTaxLabel(taxSublabels)}
                  </p>
                  <p className="text-xs" style={{ color: theme.gray[500] }}>
                    {taxLabel}
                  </p>
                </div>
                <p className="text-lg font-semibold" style={{ color: theme.text }}>
                  {formatCurrency(data.taxBreakdown.total)}
                </p>
              </div>
            ) : (
              <>
                {/* CGST */}
                <div className="flex justify-between items-center p-3 rounded-lg" style={{ background: theme.gray[50] }}>
                  <div>
                    <p className="text-sm font-medium" style={{ color: theme.text }}>
                      {taxSublabels[0] || 'CGST'}
                    </p>
                    <p className="text-xs" style={{ color: theme.gray[500] }}>
                      Central GST
                    </p>
                  </div>
                  <p className="text-lg font-semibold" style={{ color: theme.text }}>
                    {formatCurrency(data.taxBreakdown.cgst)}
                  </p>
                </div>

                {/* SGST */}
                <div className="flex justify-between items-center p-3 rounded-lg" style={{ background: theme.gray[50] }}>
                  <div>
                    <p className="text-sm font-medium" style={{ color: theme.text }}>
                      {taxSublabels[1] || 'SGST'}
                    </p>
                    <p className="text-xs" style={{ color: theme.gray[500] }}>
                      State GST
                    </p>
                  </div>
                  <p className="text-lg font-semibold" style={{ color: theme.text }}>
                    {formatCurrency(data.taxBreakdown.sgst)}
                  </p>
                </div>

                {/* IGST */}
                <div className="flex justify-between items-center p-3 rounded-lg" style={{ background: theme.gray[50] }}>
                  <div>
                    <p className="text-sm font-medium" style={{ color: theme.text }}>
                      {taxSublabels[2] || 'IGST'}
                    </p>
                    <p className="text-xs" style={{ color: theme.gray[500] }}>
                      Integrated GST
                    </p>
                  </div>
                  <p className="text-lg font-semibold" style={{ color: theme.text }}>
                    {formatCurrency(data.taxBreakdown.igst)}
                  </p>
                </div>

                {/* Service Tax */}
                <div className="flex justify-between items-center p-3 rounded-lg" style={{ background: theme.gray[50] }}>
                  <div>
                    <p className="text-sm font-medium" style={{ color: theme.text }}>
                      Service Tax
                    </p>
                    <p className="text-xs" style={{ color: theme.gray[500] }}>
                      Service charges
                    </p>
                  </div>
                  <p className="text-lg font-semibold" style={{ color: theme.text }}>
                    {formatCurrency(data.taxBreakdown.serviceTax)}
                  </p>
                </div>
              </>
            )}

            {/* Total */}
            <div
              className="flex justify-between items-center p-3 rounded-lg border-t-2 mt-2"
              style={{ borderColor: theme.primary }}
            >
              <p className="text-sm font-bold" style={{ color: theme.text }}>
                Total Tax
              </p>
              <p className="text-xl font-bold" style={{ color: theme.primary }}>
                {formatCurrency(data.totalTax)}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FinancialSummary;
